/**
 * @file app.config.ts
 * @description Application-level dependency injection configuration.
 * Registers routing, HTTP client, global error handling, OAuth code
 * consumption at bootstrap, and browser event listeners.
 */
import { isPlatformBrowser } from '@angular/common';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  Injectable,
  PLATFORM_ID,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  SecurityContext,
} from '@angular/core';
import { provideClientHydration, withEventReplay } from '@angular/platform-browser';
import { provideMarkdown, SANITIZE } from 'ngx-markdown';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

import { provideTranslations } from '@kirigami/shared/i18n';
import { GTM_CONTAINER_ID, SEO_CANONICAL_ORIGIN } from '@kirigami/platform/browser';
import { REGISTRY_API_BASE_URL } from '@kirigami/data-access/registry-api-client';
import { KB_API_BASE_URL } from '@kirigami/data-access/kb-adapters-web';

import { routes } from './app.routes';
import { EN_DICTIONARY } from './i18n';
import { environment } from '../environments/environment';
import { authInterceptor } from './interceptors/auth.interceptor';
import { authErrorInterceptor } from './interceptors/auth-error.interceptor';
import { AuthService } from './services/auth.service';
import { WordDialogBridgeService } from './services/word-dialog-bridge.service';

/**
 * Resolves the Registry API *origin* (scheme + host, no `/v1` suffix).
 *
 * `environment.apiBaseUrl` historically included the `/v1` segment so the
 * legacy services could do string concatenation. The shared
 * `REGISTRY_API.*` constants now own the version prefix, so adapters need
 * the origin without it. Strip a trailing `/v1` (or `/v1/`) here once, at
 * the provider boundary.
 */
function resolveRegistryApiOrigin(): string {
  return environment.apiBaseUrl.replace(/\/v1\/?$/, '');
}

/**
 * Global error handler that catches unhandled errors across the application.
 * Logs errors with a consistent prefix for easy filtering.
 * // TODO [INTEGRATION]: Send errors to a monitoring service (Sentry, LogRocket, etc.)
 */
@Injectable()
class GlobalErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    console.error('[Kirigami Error]', error);
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    // Keeps the prerendered DOM instead of tearing it down and rebuilding it,
    // and replays a click or keypress that lands before the bundle is ready.
    provideClientHydration(withEventReplay()),
    // Forward navigations land at the top of the destination page; browser
    // back/forward restore the previous scroll position. `anchorScrolling`
    // is what makes a URL fragment win over that restore: without it the
    // router scrolled every in-page jump straight back to the top, so the
    // skip links and the footer's own anchors did nothing.
    provideRouter(
      routes,
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
    ),
    // English is bundled rather than fetched, so the landing paints its real
    // copy on the first frame. Extra locales go in `loaders` and load on demand.
    provideTranslations({ defaultLocale: 'en', dictionary: EN_DICTIONARY }),
    provideHttpClient(withInterceptors([authInterceptor, authErrorInterceptor])),
    { provide: REGISTRY_API_BASE_URL, useFactory: resolveRegistryApiOrigin },
    { provide: KB_API_BASE_URL, useValue: environment.kbapiBaseUrl },
    { provide: GTM_CONTAINER_ID, useValue: environment.gtm_id },
    { provide: SEO_CANONICAL_ORIGIN, useValue: 'https://kirigami.ai' },
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    provideMarkdown({ sanitize: { provide: SANITIZE, useValue: SecurityContext.HTML } }),
    /**
     * Checks for a pending OAuth authorization code at bootstrap.
     * If auth-callback.html stashed a code in storage, this exchanges
     * it for an access token before the app renders any guarded route.
     *
     * When kirigami-app was loaded inside a Word Add-in dialog
     * (`/login?host=word`), this also bridges the resulting session back
     * to the parent task pane via `Office.context.ui.messageParent` so
     * the add-in can close the dialog and pick up the session — instead
     * of leaving the user on the web landing page inside the dialog.
     */
    provideAppInitializer(() => {
      // Nothing here has meaning during the prerender pass: there is no URL to
      // read a code from, no storage to write a session to, and no Word host.
      if (!isPlatformBrowser(inject(PLATFORM_ID))) {
        return;
      }
      const authService = inject(AuthService);
      const wordDialogBridge = inject(WordDialogBridgeService);
      // Persist the `?host=word` marker if we booted straight into a
      // dialog URL (e.g. auth-callback redirected to `/`).
      wordDialogBridge.detectAndRemember();

      return lastValueFrom(authService.consumeAuthCode())
        .then((user) => {
          if (!user || !wordDialogBridge.isInWordDialog()) return;
          return wordDialogBridge
            .notifyLoginSuccess({
              accessToken: authService.getAccessToken() ?? '',
              email: user.email,
              displayName: user.displayName,
            })
            .catch((err) => {
              console.warn('[appConfig] Word dialog bridge failed:', err);
              wordDialogBridge.clear();
            });
        })
        .then(
          () => undefined,
          () => undefined,
        );
    }),
  ],
};
